import React, { Suspense, useContext, useEffect } from "react";
import { Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Stepper } from "./components/stepper";
import { useStepsController } from "./hooks/steps-controller";
import { LoadingSpinner } from "../components/loading-spinner";
import { useTranslations } from "../hooks/use-translations";
import { usePersonalAccountPermissions } from "../hooks/use-personal-account-permissions";
import { store } from "../components/Store";
import { useCreateProfileTutorial } from "../components/personal-account-tutorials/hooks/use-create-profile-tutorial";

const Lazy = {
	MainForm: React.lazy(() => import("./main-form")),
	AccountValidation: React.lazy(() => import("./account-validation")),
	FinalForm: React.lazy(() => import("./final-form")),
	Welcome: React.lazy(() => import("./welcome")),
};
/**
 * Componente que renderiza el router para la creaci n de una cuenta personal.
 * Este componente utiliza los hooks de traducciones y permisos para renderizar
 * las diferentes secciones de la creaci n de la cuenta.
 * @returns {React.ReactElement} Un React Element que renderiza el router para
 * la creaci n de una cuenta personal.
 */
export const PersonalAccountSignUpRouter = () => {
	const globalState = useContext(store);
	const { state } = globalState;
	const isAuth = state?.sesionActiva === "SI";
	const username = state?.username;
	const texts = useTranslations({ section: "crear_cuenta_personal" });
	const location = useLocation();
	const history = useHistory();
	const stepsController = useStepsController();
	const steps = stepsController.steps;
	const isWelcomePath = location.pathname === "/crear-perfil/bienvenida";
	const currentStep = steps.find((step) => step.path === location.pathname)?.index || 1;
	const personalAccountPermissions = usePersonalAccountPermissions();
	const canCreateAccounts = personalAccountPermissions.hasPermission("create");
	const canViewAccount = personalAccountPermissions.hasPermission("view");
	const canEditAccount = personalAccountPermissions.hasPermission("edit");
	const createProfileTutorial = useCreateProfileTutorial();

	useEffect(() => {
		if(!isAuth) return;
		if(!canCreateAccounts) return;
		if (!username) return;
		if (createProfileTutorial.seen ) return;

		createProfileTutorial.markSeen();
		globalState.dispatch({
			type: "CHANGE",
			path: "personalAccountTutorials.createProfileVisible",
			value: true,
		});
	}, [createProfileTutorial.seen, username, isAuth, canCreateAccounts]);

	const backPaths = {
		"/crear-perfil/paso-1": {
			action:()=>{
				history.replace(isAuth ? "/home" : "/login");
			}
		},
		"/crear-perfil/paso-2": {
			action:()=>{
				history.replace("/crear-perfil/paso-1");
			}
		},
	}
	const handleBack = () => {
		const pathnameAction = backPaths[location.pathname]?.action;
		if(pathnameAction){
			pathnameAction();
		}
	};

	return (
		<div className="personal-account-sign-up d-flex">
			<div className="bg-background-shape"></div>
			<Suspense
				fallback={
					<div className="w-100 d-flex justify-content-center">
						<LoadingSpinner size={40} className={"onsecondary--color m-3"} />
					</div>
				}
			>
				<div className="page-transition container flex-grow-1  p-3" style={{ zIndex: 1 }}>
					{!isWelcomePath && (
						<div className="row p-0 m-0 align-items-center">
							<div className="col-12 col-lg-4">
								<div className="w-100 d-flex justify-content-start align-items-center my-4 m-2">
									{currentStep <= 2 && (
										<button
											className="back-button p-2 d-flex justify-content-center align-items-center focusable-primary"
											onClick={handleBack}
										>
											<Icon
												icon="teenyicons:arrow-left-solid"
												width={28}
												className="mx-1"
											/>
											{texts?.boton?.volver || "Volver"}
										</button>
									)}
								</div>
							</div>
							<div className="col-12 col-lg-4 d-flex justify-content-center">
								<Stepper
									steps={steps}
									currentStep={currentStep}
									onClickStep={(step) => {
										history.push(step.path || "/");
									}}
								/>
							</div>
							<div className="col-12 col-lg-4"></div>
						</div>
					)}

					<Switch>
						<Route exact path="/crear-perfil">
							<Redirect to="/crear-perfil/paso-1" />
						</Route>
						<Route exact path="/crear-perfil/paso-1" component={Lazy.MainForm} />
						<Route
							exact
							path="/crear-perfil/paso-2"
							component={Lazy.AccountValidation}
						/>
						<Route exact path="/crear-perfil/paso-3" component={Lazy.FinalForm} />
						<Route exact path="/crear-perfil/bienvenida" component={Lazy.Welcome} />
					</Switch>
				</div>
			</Suspense>
		</div>
	);
};
